import { InfoIcon } from '@/imports/ui/chakra/feather';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  AlertTitle,
  Button,
  ButtonProps,
  HStack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';

export const ConfirmDialog: React.FC<{
  body: string;
  confirmButtonText: string;
  cancelButtonText?: string;
  alternativeButtonText?: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: null | (() => Promise<unknown> | void);
  onClickAlternativeOption?: () => Promise<unknown> | void;
  title: string;
  heading?: string | null;
  shouldHideCancelButton?: boolean;
  shouldCloseOnConfirmation?: boolean;
  confirmButtonColorScheme?: ButtonProps['colorScheme'];
  alertText?: string | undefined;
}> = ({
  body,
  confirmButtonText,
  cancelButtonText,
  isOpen,
  onClose,
  onConfirm,
  shouldHideCancelButton,
  title,
  heading,
  shouldCloseOnConfirmation = true,
  confirmButtonColorScheme,
  alternativeButtonText,
  onClickAlternativeOption,
  alertText,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const cancelButton = React.useRef(null);

  const onClickConfirm = async () => {
    if (onConfirm === null) {
      onClose();
      return;
    }
    setIsLoading(true);
    try {
      await onConfirm();
      if (shouldCloseOnConfirmation) {
        onClose();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog
      leastDestructiveRef={cancelButton}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      isCentered
    >
      <AlertDialogOverlay />

      <AlertDialogContent>
        <AlertDialogHeader border="1px solid" borderColor="strokePrimary.500">{title}</AlertDialogHeader>

        <AlertDialogCloseButton />
        <AlertDialogBody minH="50px">
          {heading && (
            <Text fontWeight="semibold" fontSize="20px" mb="2" mt="2">
              {heading}
            </Text>
          )}

           <Text size="sm" fontWeight="medium">{body}</Text>
            {(alertText ?? '').length > 0 && ( <Alert status="error" borderRadius="md" mt={4} border="0">
                <InfoIcon boxSize={5} color="red.500" mr={0} mt="2px" />
              <AlertTitle mr={2}></AlertTitle>
             
                <AlertDescription color="red.500" fontWeight="medium">
                  {alertText}
                </AlertDescription>      
            </Alert>)}
            
          </AlertDialogBody>

        <AlertDialogFooter>
          <HStack spacing="4">
            {shouldHideCancelButton ? null : (
              <Button
                ref={cancelButton}
                type="button"
                variant="outline"
                colorScheme="primary"
                borderColor="primary.500"
                onClick={onClose}
                isDisabled={isLoading}
              >
                {cancelButtonText ?? 'Cancel'}
              </Button>
            )}

            {alternativeButtonText && (
              <Button
                ref={cancelButton}
                variant="outline"
                colorScheme="primary"
                borderColor="primary.500"
                onClick={onClickAlternativeOption}
                isDisabled={isLoading}
              >
                {alternativeButtonText}
              </Button>
            )}

            <Button
              onClick={onClickConfirm}
              type="button"
              isLoading={isLoading}
              // not an ideal way to do this but i'm not sure what else to do
              colorScheme={confirmButtonColorScheme}
            >
              {confirmButtonText}
            </Button>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
